interface AIAnswerBlockProps {
  question: string;
  answer: string;
  highlights?: string[];
}

export default function AIAnswerBlock({ question, answer, highlights = [] }: AIAnswerBlockProps) {
  const renderAnswer = (text: string) => {
    if (!highlights.length) return <span>{text}</span>;
    const parts = text.split(new RegExp(`(${highlights.map(h => h.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'gi'));
    return (
      <>
        {parts.map((part, i) =>
          highlights.some((h) => h.toLowerCase() === part.toLowerCase()) ? (
            <strong key={i} className="text-orange-600 font-bold">
              {part}
            </strong>
          ) : (
            <span key={i}>{part}</span>
          )
        )}
      </>
    );
  };

  return (
    <section className="bg-slate-50 py-20 relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-px bg-slate-200" />
      <div className="absolute bottom-0 left-0 w-full h-px bg-slate-200" />

      <div className="relative z-10 max-w-4xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-orange-50 border border-orange-200 mb-6">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-sparkling-fill text-orange-500 text-sm" />
            </div>
            <span className="text-orange-700 text-xs font-bold uppercase tracking-[0.15em]">
              AI Knowledge Block
            </span>
          </div>
          <h2 className="font-display text-3xl lg:text-4xl font-black text-slate-900">
            {question}
          </h2>
        </div>

        {/* Answer card */}
        <div className="relative rounded-2xl border-l-4 border-l-orange-500 border border-slate-200 bg-white overflow-hidden">
          {/* Robot icon watermark */}
          <div className="absolute top-4 right-5 w-10 h-10 flex items-center justify-center opacity-10">
            <i className="ri-robot-2-fill text-slate-900 text-3xl" />
          </div>

          <div className="px-8 lg:px-10 py-8 lg:py-10">
            <div className="flex items-center gap-2 mb-5">
              <div className="w-2 h-2 rounded-full bg-orange-500" />
              <span className="text-orange-600 text-xs font-semibold">Secure2Ware Knowledge Base</span>
              <span className="text-slate-400 text-xs">· Verified Answer</span>
            </div>

            <p className="text-slate-700 text-lg leading-relaxed">{renderAnswer(answer)}</p>

            <div className="flex flex-wrap gap-2 mt-7 pt-6 border-t border-slate-100">
              {highlights.slice(0, 5).map((h) => (
                <span
                  key={h}
                  className="px-3 py-1 rounded-full text-xs font-medium bg-orange-50 border border-orange-200 text-orange-700"
                >
                  {h}
                </span>
              ))}
              <span className="px-3 py-1 rounded-full text-xs font-medium bg-slate-100 border border-slate-200 text-slate-600">
                <i className="ri-mic-fill mr-1" />Voice Search Ready
              </span>
            </div>
          </div>
        </div>

        <p className="text-center text-slate-400 text-xs mt-4">
          AI-extractable content · Optimized for Google AI Overviews &amp; voice search
        </p>
      </div>
    </section>
  );
}
